import {
  RocketLaunch,
  Megaphone,
  Target,
  CreditCard,
  ChatTeardropText,
  type Icon,
} from '../design-system/icons';
import type { CampaignFormData } from './campaignSchema';

// Usage-rights duration bounds (days). Single source for the Zod rule, the
// input's native min/max, and the helper text. Max is 3 years.
export const USAGE_RIGHTS_MIN_DAYS = 30;
export const USAGE_RIGHTS_MAX_DAYS = 1095;

// Labels used to name the offending field in the "can't continue" toast.
export const FIELD_LABELS: Partial<Record<keyof CampaignFormData, string>> = {
  platform: 'Platform',
  name: 'Campaign name',
  primaryGoal: 'Primary goal',
  brief: 'Brief',
  totalCreators: 'Number of creators',
  creatorCategories: 'Creator categories',
  creatorGenders: 'Creator genders',
  creatorGenderNotes: 'Creator gender notes',
  tierPresets: 'Creator tiers',
  ageMin: 'Minimum age',
  ageMax: 'Maximum age',
  gender: 'Audience gender',
  language: 'Language',
  region: 'Region',
  deliverables: 'Content types',
  deliverables_text: 'Deliverables description',
  usageRights: 'Usage rights',
  totalBudget: 'Total budget',
  startDate: 'Start date',
  payoutModel: 'Payout model',
  primaryFocus: 'Primary focus',
  videoTitle: 'Video title',
  videoDescription: 'Video description',
  secondaryFocus: 'Secondary focus',
  cta: 'Call to action',
  dos: "Do's",
  donts: "Don'ts",
  sampleVideoUrl: 'Reference video',
  products: 'Products',
};

export const CREATOR_CATEGORIES = [
  'Finance',
  'Business',
  'Tech',
  'Lifestyle',
  'Health',
  'Education',
  'Beauty',
  'Food',
  'Travel',
];

export const TIERS = [
  { value: '1000-10000', label: '1K – 10K', desc: 'Nano Creator' },
  { value: '10000-50000', label: '10K – 50K', desc: 'Micro Creator' },
  { value: '50000-100000', label: '50K – 100K', desc: 'Macro Creator' },
  { value: '100000-500000', label: '100K – 500K', desc: 'Mid-tier Creator' },
  { value: '500000-1000000', label: '500K – 1M', desc: 'Mega Creator' },
  { value: '1000000-10000000', label: '1M+', desc: 'Celebrity Creator' },
];

export const REGIONS = ['India', 'USA', 'UAE', 'UK', 'Global'];
export const GENDER_OPTIONS = ['Male', 'Female', 'Non-binary'];
export const LANGUAGE_OPTIONS = [
  'English',
  'Hindi',
  'Tamil',
  'Telugu',
  'Kannada',
  'Malayalam',
  'Bengali',
  'Marathi',
  'Gujarati',
  'Arabic',
  'Spanish',
];

export interface PrimaryGoal {
  value: string;
  icon: Icon;
  label: string;
  desc: string;
}

export const PRIMARY_GOALS: PrimaryGoal[] = [
  {
    value: 'product_launch',
    icon: RocketLaunch,
    label: 'Product Launch',
    desc: 'Introduce a new product or feature to market',
  },
  {
    value: 'brand_awareness',
    icon: Megaphone,
    label: 'Brand Awareness',
    desc: 'Expand views and grow recognition',
  },
  {
    value: 'lead_generation',
    icon: Target,
    label: 'Lead Generation',
    desc: 'Capture new potential customers',
  },
  {
    value: 'sales',
    icon: CreditCard,
    label: 'Drive Sales',
    desc: 'Convert audience into paying customers',
  },
  {
    value: 'engagement',
    icon: ChatTeardropText,
    label: 'Engagement',
    desc: 'Build community and spark interaction',
  },
];

// UI cap for the Multi-Product Campaigns catalog. Lives here (not
// helpers.ts, which imports `storage` from @discovr/common — a
// Firebase-touching module at import time) specifically so campaignSchema.ts
// can import it without dragging that chain into what should stay a "pure"
// validation module reachable from any context, including tests that never
// render a real component tree. The schema still accepts up to 50 for
// back-compat with older drafts; the create-campaign UI limits new rows to
// keep the list manageable.
export const MAX_PRODUCTS = 10;

export interface WizardStep {
  num: number;
  title: string;
  sub: string;
}

// Step ids are stable regardless of which steps a host renders: stepFields,
// FIELD_TO_STEP, computeStepComplete and persisted drafts are all keyed by them.
export const CONNECT_STEP = 5;

// Figma "Create campaign" flow: 6-step top stepper.
// Connect (5) is a host-provided slot, skipped when the host passes none.
// Submit (6) = review + launch → success.
export const STEPS: WizardStep[] = [
  { num: 1, title: 'Basics', sub: 'Name, goal' },
  { num: 2, title: 'Audience', sub: 'Targeting' },
  { num: 3, title: 'Deliverables', sub: 'Content & budget' },
  { num: 4, title: 'Brief', sub: 'Strategy' },
  { num: CONNECT_STEP, title: 'Connect', sub: 'Tools' },
  { num: 6, title: 'Submit', sub: 'Review & launch' },
];

/* ── Tailwind shorthand tokens matching the legacy CSS ────────── */
// input → matches .ncw-input: border, focus ring, sm text
export const inp =
  'w-full px-3.5 py-2.5 text-body-sm-regular text-foreground bg-card border border-border rounded-lg outline-none focus:border-foreground focus:ring-[3px] focus:ring-ring/8 placeholder:text-muted-foreground transition-[border-color] font-[inherit] box-border';
// select → matches .ncw-select: same + chevron bg-image removed (custom arrow via appearance-none)
export const sel = 'px-3.5 py-2 pr-8 text-body-sm-regular text-foreground bg-card border border-border rounded-lg outline-none focus:border-foreground focus:ring-[3px] focus:ring-ring/8 transition-[border-color] cursor-pointer appearance-none font-[inherit]';
export const lbl = 'text-body-sm-medium text-foreground';
export const hint = 'text-[12px] font-normal text-muted-foreground ml-2';
export const err = 'text-caption-regular text-foreground-destructive-default mt-1';

// A field that fails validation gets the design-system destructive border token,
// including while focused (the focus-visible variant out-specifies the Input's focus border).
export function invalidBorder(invalid: boolean): string {
  if (!invalid) return '';
  return 'border-border-destructive-default focus-visible:border-border-destructive-default';
}
